import { Injectable, Signal, computed, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { FeatureFlag, FeatureFlagsOn, FeatureFlagsOnReader } from '../shared/feature-flags';
import { AuthService } from './auth.service';
import { HttpClientService } from './http-client.service';

/**
 * Which feature flags are on for whoever uses the site: a visitor, a member or beheer (docs/FEATURE_FLAGS.md). The
 * list is read once when the service is first used, and again when someone signs in or out; navigating reads nothing,
 * and neither does the refreshed token every answer brings. Superbeheer has SuperAdminFeatureFlagService.
 */
@Injectable({ providedIn: 'root' })
export class FeatureFlagService {
  private readonly http = inject(HttpClientService);
  private readonly auth = inject(AuthService);

  /** Changes on sign-in and sign-out (and a change of role), not on every refreshed token. */
  private readonly signedInAs = computed(() => {
    const user = this.auth.currentUser();

    return user === null ? '' : `${user.email}|${user.role}`;
  });

  private readonly reader = new FeatureFlagsOnReader(this.signedInAs, () =>
    this.http.get<FeatureFlagsOn>('/feature-flags'),
  );

  isOn(flag: FeatureFlag): Signal<boolean> {
    return this.reader.isOn(flag);
  }

  /** For a route guard: completes once the flags of whoever is signed in now are known. */
  whenKnown(): Observable<void> {
    return this.reader.whenKnown();
  }
}
